import React, { useEffect, useState } from "react";
import { NavLink, Link, useLocation, useNavigate } from "@/lib/nav";
import { useTranslation } from "react-i18next";
import { Menu, X, ShieldCheck, LogOut, User as UserIcon, Crown, Bell, Briefcase, MessageSquare, Mail, Network, Compass, Inbox } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationsContext";
import { isAdminRole, isOwner } from "@/lib/api";
import LangToggle from "@/components/LangToggle";

const PUBLIC_NAV = [
  { to: "/", label: "nav.home", testid: "nav-home" },
  { to: "/chi-siamo", label: "nav.about", testid: "nav-chi-siamo" },
  { to: "/casi-duso", label: "nav.useCases", testid: "nav-casi-duso" },
  { to: "/accesso", label: "nav.pricing", testid: "nav-prezzi" },
  { to: "/demo", label: "nav.demo", testid: "nav-demo" },
  { to: "/explorer", label: "nav.explorer", testid: "nav-explorer" },
  { to: "/install", label: "nav.install", testid: "nav-install" },
];
const APP_NAV_BASE = [
  { to: "/app", label: "nav.workspace", testid: "nav-workspace" },
  { to: "/certify", label: "nav.certify", testid: "nav-certify" },
  { to: "/pulse", label: "nav.pulse", testid: "nav-pulse" },
  { to: "/lns", label: "nav.lns", testid: "nav-lns" },
  { to: "/explorer", label: "nav.explorer", testid: "nav-explorer" },
];
const APP_NAV_ADMIN = [
  { to: "/dashboard", label: "nav.dashboard", testid: "nav-dashboard" },
];
const OWNER_NAV = [
  { to: "/console", label: "nav.console", testid: "nav-console" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { counts } = useNotifications();
  const { t } = useTranslation();
  const loc = useLocation();
  const nav = useNavigate();
  const { user, isAuth, logout } = useAuth();

  useEffect(() => setOpen(false), [loc.pathname]);
  useEffect(() => {
    document.body.classList.toggle("has-bottom-nav", isAuth);
    return () => document.body.classList.remove("has-bottom-nav");
  }, [isAuth]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const badgeFor = (to) => (to === "/certify" ? counts.certify_unread : to === "/pulse" ? counts.pulse_unread : to === "/console" ? counts.leads_unread : 0);
  const totalUnread = (counts.certify_unread || 0) + (counts.pulse_unread || 0);

  const NAV = isAuth ? [...APP_NAV_BASE, ...(isAdminRole(user.role) ? APP_NAV_ADMIN : []), ...(isOwner(user) ? OWNER_NAV : [])] : PUBLIC_NAV;
  const doLogout = () => { logout(); nav("/"); };

  const ICONS = { "/app": Briefcase, "/certify": Mail, "/pulse": MessageSquare, "/lns": Network, "/explorer": Compass, "/console": Inbox };
  // Mobile bottom tab bar: the 5 most-used destinations for an app-like feel.
  const BOTTOM = (isAuth ? [...APP_NAV_BASE, ...(isOwner(user) ? OWNER_NAV : [])] : []).slice(0, 5);

  return (<>
    <header className={`sticky top-0 z-50 border-b transition-colors duration-300 ${scrolled || isAuth ? "border-white/10 bg-black/70 backdrop-blur-xl" : "border-transparent bg-transparent"}`} data-testid="site-header">
      <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between px-5 lg:px-8">
        <Link to={isAuth ? "/app" : "/"} className="group flex items-center gap-2.5" data-testid="brand-logo">
          <div className="relative flex h-8 w-8 items-center justify-center">
            <img src="/favicon-192x192.png" alt="Lattice Network" className="h-8 w-8 rounded-sm object-contain" />
            <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full lp-b lt-pulse-dot" />
          </div>
          <div className="leading-none">
            <div className="font-display text-[15px] font-700 tracking-tight">LATTICE</div>
            <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#666]">sovereign · rust</div>
          </div>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={n.testid}
              className={({ isActive }) => `relative px-3.5 py-2 font-mono text-[12px] uppercase tracking-wider transition-colors ${isActive ? "lp-c" : "text-[#a3a3a3] hover:text-white"}`}>
              {t(n.label)}
              {badgeFor(n.to) > 0 && <span data-testid={`nav-badge-${n.testid}`} className="absolute right-0 top-1 flex h-4 min-w-4 items-center justify-center rounded-full lp-b px-1 text-[9px] font-700 text-black">{badgeFor(n.to) > 99 ? "99+" : badgeFor(n.to)}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <LangToggle className="hidden sm:inline-flex" />
          {isAuth && (
            <Link to="/security" data-testid="nav-security" className="hidden items-center gap-1.5 border border-[#262626] bg-[#0d0d0d] px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-[#a3a3a3] lp-h-bd hover:text-white sm:inline-flex">
              <ShieldCheck className="h-3.5 w-3.5" /> {t("nav.security")}
            </Link>
          )}
          {isAuth && (
            <Link to="/license" data-testid="nav-license" className="hidden items-center gap-1.5 border border-[#262626] bg-[#0d0d0d] px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-[#a3a3a3] lp-h-bd hover:text-white sm:inline-flex">
              <ShieldCheck className="h-3.5 w-3.5" /> {t("nav.license")}
            </Link>
          )}
          {isAuth && (
            <button onClick={() => nav("/certify")} data-testid="notif-bell" aria-label="notifiche" className="relative inline-flex items-center justify-center border border-[#262626] bg-[#0d0d0d] p-2 text-[#a3a3a3] transition-colors lp-h-bd hover:text-white">
              <Bell className="h-4 w-4" />
              {totalUnread > 0 && <span data-testid="notif-badge" className="absolute -right-1.5 -top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full lp-b px-1 text-[10px] font-700 text-black">{totalUnread > 99 ? "99+" : totalUnread}</span>}
            </button>
          )}
          {isAuth ? (
            <div className="hidden items-center gap-3 sm:flex">
              <div className="flex items-center gap-2 border border-[#262626] bg-[#0d0d0d] px-3 py-1.5">
                {isAdminRole(user.role) ? <Crown className="h-3.5 w-3.5 text-[#ff9100]" /> : <UserIcon className="h-3.5 w-3.5 lp-c" />}
                <span className="font-mono text-[11px] lp-c" data-testid="header-user-lns">{user.lns}</span>
              </div>
              <button onClick={doLogout} data-testid="logout-btn" className="inline-flex items-center gap-2 border border-white/15 px-3 py-2 font-mono text-[12px] uppercase tracking-wider text-[#a3a3a3] hover:text-white">
                <LogOut className="h-3.5 w-3.5" /> {t("nav.logout")}
              </button>
            </div>
          ) : (
            <Link to="/login" data-testid="nav-login" className="hidden items-center gap-2 border lp-bd lp-b px-4 py-2 font-mono text-[12px] uppercase tracking-wider text-white hover:bg-[#3fa862] sm:inline-flex">
              <ShieldCheck className="h-3.5 w-3.5" /> {t("nav.login")}
            </Link>
          )}
          <button className="text-white lg:hidden" onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-toggle" aria-label="menu">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-white/10 bg-black/95 lg:hidden" data-testid="mobile-menu">
          <div className="flex flex-col px-5 py-3">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`m-${n.testid}`}
                className={({ isActive }) => `flex items-center justify-between border-b border-white/5 py-3 font-mono text-sm uppercase tracking-wider ${isActive ? "lp-c" : "text-[#a3a3a3]"}`}>
                <span>{t(n.label)}</span>
                {badgeFor(n.to) > 0 && <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full lp-b px-1.5 text-[11px] font-700 text-black">{badgeFor(n.to) > 99 ? "99+" : badgeFor(n.to)}</span>}
              </NavLink>
            ))}
            {isAuth ? (
              <button onClick={doLogout} data-testid="m-logout" className="py-3 text-left font-mono text-sm uppercase tracking-wider text-[#ff9a9a]">{t("nav.logout")} ({user.lns})</button>
            ) : (
              <NavLink to="/login" className="py-3 font-mono text-sm uppercase tracking-wider lp-c">{t("nav.login")}</NavLink>
            )}
            <div className="py-3"><LangToggle /></div>
          </div>
        </div>
      )}
    </header>

    {isAuth && BOTTOM.length > 0 && (
      <nav data-testid="mobile-bottom-nav" className="fixed bottom-0 left-0 right-0 z-50 grid border-t border-white/10 bg-black/90 backdrop-blur-xl lg:hidden" style={{ gridTemplateColumns: `repeat(${BOTTOM.length}, minmax(0, 1fr))`, paddingBottom: "env(safe-area-inset-bottom)" }}>
        {BOTTOM.map((n) => {
          const Icon = ICONS[n.to] || Briefcase;
          const b = badgeFor(n.to);
          return (
            <NavLink key={n.to} to={n.to} data-testid={`bn-${n.testid}`}
              className={({ isActive }) => `relative flex flex-col items-center gap-1 py-2.5 font-mono text-[9px] uppercase tracking-wider ${isActive ? "lp-c" : "text-[#888]"}`}>
              <Icon className="h-5 w-5" />
              <span>{t(n.label)}</span>
              {b > 0 && <span className="absolute right-[22%] top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full lp-b px-1 text-[9px] font-700 text-black">{b > 99 ? "99+" : b}</span>}
            </NavLink>
          );
        })}
      </nav>
    )}
  </>
  );
}
